import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  CheckCircle2,
  XCircle,
  Clock,
  PauseCircle,
  AlertTriangle,
  SkipForward,
  Image as ImageIcon,
  Building2,
  Wrench,
  Calendar
} from 'lucide-react';
import type { ResourceTaskItem, ResourceTaskStatus } from '../../../../services/projectService';

interface Props {
  task: ResourceTaskItem | null;
  onClose: () => void;
}

const STATUS_CONFIG: Record<ResourceTaskStatus, { label: string; color: string; icon: any }> = {
  pass: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  fail: { label: 'Failed', color: 'bg-rose-500/10 text-rose-700 border-rose-200', icon: XCircle },
  inprogress: { label: 'In Progress', color: 'bg-cyan-500/10 text-cyan-700 border-cyan-200', icon: Clock },
  hold: { label: 'On Hold', color: 'bg-amber-500/10 text-amber-700 border-amber-200', icon: PauseCircle },
  due: { label: 'Due', color: 'bg-amber-500/10 text-amber-700 border-amber-200', icon: Clock },
  overdue: { label: 'Overdue', color: 'bg-rose-500/10 text-rose-700 border-rose-200', icon: AlertTriangle },
  unassigned: { label: 'Unassigned', color: 'bg-slate-500/10 text-slate-700 border-slate-200', icon: PauseCircle },
  skip: { label: 'Skipped', color: 'bg-slate-500/10 text-slate-600 border-slate-200', icon: SkipForward }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatMinutes(min: number): string {
  if (!min || min <= 0) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default function TaskDetailDrawer({ task, onClose }: Props) {
  return (
    <AnimatePresence>
      {task && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 220, damping: 28 }}
            onClick={e => e.stopPropagation()}
            className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-teal-600">Task detail</p>
                <h2 className="text-base font-bold text-slate-900">{task.checkpointName}</h2>
                <p className="mt-1 text-[11px] text-slate-500">
                  {task.frequency ? `${task.frequency} · ` : ''}Task ID {task.taskId.slice(-8)}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <StatusBadge status={task.status} />

              <Section title="Location">
                <Row icon={Building2} label="Asset" value={task.projectAssetName ?? '—'} />
                <Row icon={Wrench} label="Equipment" value={task.equipmentName ?? '—'} />
              </Section>

              <Section title="Timeline">
                <Row icon={Calendar} label="Planned" value={formatDate(task.plannedDate ?? task.startDate)} />
                <Row icon={Clock} label="Started" value={formatDate(task.taskStartDate)} />
                {task.passDateTime && <Row icon={CheckCircle2} label="Completed" value={formatDate(task.passDateTime)} />}
                {task.failDateTime && <Row icon={XCircle} label="Failed at" value={formatDate(task.failDateTime)} />}
                <Row icon={Clock} label="Working time" value={formatMinutes(task.actualWorkingMinutes)} />
              </Section>

              {task.outcome && (
                <Section title="Outcome">
                  <p className="text-sm text-slate-700">{task.outcome}</p>
                </Section>
              )}

              {task.remarks && (
                <Section title="Remarks">
                  <p className="rounded-lg bg-slate-50 p-2 text-xs text-slate-700">{task.remarks}</p>
                </Section>
              )}

              {task.hasIssue && (
                <Section title="Issue">
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs">
                    <div className="flex items-center gap-2 font-semibold text-rose-700">
                      <AlertTriangle className="h-4 w-4" />
                      {task.issueStatus ?? 'Open'}
                    </div>
                    <p className="mt-1 text-rose-600">An issue is recorded against this task.</p>
                  </div>
                </Section>
              )}

              {(task.beforeImages.length > 0 || task.afterImages.length > 0) && (
                <Section title="Images">
                  {task.beforeImages.length > 0 && (
                    <ImageRow label="Before" images={task.beforeImages} />
                  )}
                  {task.afterImages.length > 0 && (
                    <ImageRow label="After" images={task.afterImages} />
                  )}
                </Section>
              )}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatusBadge({ status }: { status: ResourceTaskStatus }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <div className={`mb-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${cfg.color}`}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <h3 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</h3>
      <div className="rounded-xl border border-slate-100 bg-white p-2">{children}</div>
    </section>
  );
}

function Row({
  icon: Icon,
  label,
  value
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-1 py-1 text-xs">
      <span className="flex items-center gap-1.5 text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="text-right font-semibold text-slate-800">{value}</span>
    </div>
  );
}

function ImageRow({ label, images }: { label: string; images: string[] }) {
  return (
    <div className="mb-2 last:mb-0">
      <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <ImageIcon className="h-3 w-3" />
        {label} ({images.length})
      </p>
      <div className="flex flex-wrap gap-1.5">
        {images.map((url, i) => (
          <a
            key={`${label}-${i}`}
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="block h-16 w-16 overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={url} alt={`${label}-${i + 1}`} className="h-full w-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}
